import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { Device } from '@shared/models';
import {
  CreateDeviceRequest,
  CreateDevicePreRegistrationRequest,
  DeviceService,
  UpdateDeviceAuthorizationCodeRequest,
  UpdateDeviceNameRequest,
  UpdateDevicePreRegistrationRequest,
} from '@app/resources/services/device.service';

@Injectable()
export class DevicesApiService {
  private readonly deviceService = inject(DeviceService);

  getByMerchantId(merchantId: number): Observable<Device[]> {
    return this.deviceService.findByMerchantId(merchantId).pipe(
      map((result: any) => {
        const values = result?.$values ?? [];
        return values.map(mapToDevice);
      })
    );
  }

  getByIdAndMerchantId(deviceId: number, merchantId: number): Observable<Device | null> {
    return this.deviceService
      .findByIdAndMerchantId(deviceId, merchantId)
      .pipe(map((result: any) => (result ? mapToDevice(result) : null)));
  }

  create(merchantId: number, createDeviceRequest: CreateDeviceRequest): Observable<Device | null> {
    return this.deviceService.create(merchantId, createDeviceRequest).pipe(
      map((result: any) => (result ? mapToDevice(result) : null)),
      catchError((err) => {
        const message = err?.error?.message ?? 'Something went wrong while creating the device';
        return throwError(() => new Error(message));
      })
    );
  }

  createPreRegistration(
    merchantId: number,
    createDevicePreRegistrationRequest: CreateDevicePreRegistrationRequest
  ): Observable<Device | null> {
    return this.deviceService.createPreRegistration(merchantId, createDevicePreRegistrationRequest).pipe(
      map((result: any) => (result ? mapToDevice(result) : null)),
      catchError((err) => {
        const message = err?.error?.message ?? 'Something went wrong while creating the device';
        return throwError(() => new Error(message));
      })
    );
  }

  update(
    deviceId: number,
    merchantId: number,
    updateDeviceNameRequest: UpdateDeviceNameRequest
  ): Observable<Device | null> {
    return this.deviceService
      .update(deviceId, merchantId, updateDeviceNameRequest)
      .pipe(map((result: any) => (result ? mapToDevice(result) : null)));
  }

  updateAuthorizationCode(
    deviceId: number,
    merchantId: number,
    updateDeviceAuthorizationCodeRequest: UpdateDeviceAuthorizationCodeRequest
  ): Observable<Device | null> {
    return this.deviceService.updateAuthorizationCode(deviceId, merchantId, updateDeviceAuthorizationCodeRequest).pipe(
      map((result: any) => (result ? mapToDevice(result) : null)),
      catchError((err) => {
        const message = err?.error?.message ?? 'Something went wrong while updating the authorization code';
        return throwError(() => new Error(message));
      })
    );
  }

  updatePreRegistration(
    deviceId: number,
    merchantId: number,
    updateDevicePreRegistrationRequest: UpdateDevicePreRegistrationRequest
  ): Observable<Device | null> {
    return this.deviceService.updatePreRegistration(deviceId, merchantId, updateDevicePreRegistrationRequest).pipe(
      map((result: any) => (result ? mapToDevice(result) : null)),
      catchError((err) => {
        const message = err?.error?.message ?? 'Something went wrong while updating the serial number';
        return throwError(() => new Error(message));
      })
    );
  }

  deletePreRegistration(deviceId: number, merchantId: number): Observable<void> {
    return this.deviceService.deletePreRegistration(deviceId, merchantId).pipe(
      map(() => undefined),
      catchError((err) => {
        const message = err?.error?.message ?? 'Something went wrong while removing the serial number';
        return throwError(() => new Error(message));
      })
    );
  }
}

function mapToDevice(data: any): Device {
  return {
    DeviceId: data.deviceId,
    Name: data.name,
    DeviceType: data.deviceType,
    StatusType: data.statusType,
    MerchantId: data.merchantId ?? undefined,
    LocationId: data.locationId ?? undefined,
    MerchantName: data.merchantName ?? undefined,
    LocationName: data.locationName ?? undefined,
    ConnectionId: data.connectionId ?? undefined,
    SerialNumber: data.serialNumber ?? undefined,
  };
}
